// store/useProviderSyncStatus.ts
import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { Provider, ProviderAndProviderStatusResponse } from '../types';

/** One entry per (locationId, brandId) */
export type StatusEntry = {
  locationId: number;
  brandId: number;
  statuses: ProviderAndProviderStatusResponse;
};

type Key = Pick<StatusEntry, 'locationId' | 'brandId'>;

type ProviderSyncStatusStore = {
  /** statuses grouped by (locationId, brandId) */
  statusesByScope: StatusEntry[];
  /** Upsert full statuses for a given scope */
  setStatusesFor: (key: Key, next: ProviderAndProviderStatusResponse) => void;
  /** Merge/patch helper if you sometimes send partials */
  mergeStatusesFor: (
    key: Key,
    patch: Partial<ProviderAndProviderStatusResponse>
  ) => void;
  /** Read helpers */
  getStatusesFor: (key: Key) => ProviderAndProviderStatusResponse | undefined;
  /** Remove a scope entirely */
  removeStatusesFor: (key: Key) => void;

  initializedProviderConnection: Provider | null;
  setInitializedProviderConnection: (value: Provider | null) => void;
};

const TAB_ID =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

const channel: BroadcastChannel | null =
  typeof window !== 'undefined' && 'BroadcastChannel' in window
    ? new BroadcastChannel('provider-sync-status')
    : null;

function sameKey(a: Key, b: Key) {
  return a.locationId === b.locationId && a.brandId === b.brandId;
}

export const useProviderSyncStatus = create<ProviderSyncStatusStore>()(
  devtools(
    persist(
      (set, get) => ({
        statusesByScope: [],

        setStatusesFor: (key, next) => {
          set((state) => {
            const idx = state.statusesByScope.findIndex((e) => sameKey(e, key));
            const statusesByScope =
              idx === -1
                ? [...state.statusesByScope, { ...key, statuses: next }]
                : [
                    ...state.statusesByScope.slice(0, idx),
                    { ...state.statusesByScope[idx], statuses: next },
                    ...state.statusesByScope.slice(idx + 1),
                  ];
            return { statusesByScope };
          });

          channel?.postMessage({
            source: TAB_ID,
            type: 'upsert-scope',
            payload: { key, statuses: next },
          });
        },

        mergeStatusesFor: (key, patch) => {
          const current = get().statusesByScope.find((e) =>
            sameKey(e, key)
          )?.statuses;
          const merged = Array.isArray(current)
            ? // If your ProviderAndProviderStatusResponse is an array, define your own merge rule.
              // Here we do a naive shallow merge (no duplicates handling). Adjust as needed.
              ([
                ...(current ?? []),
                ...(Array.isArray(patch) ? patch : []),
              ] as ProviderAndProviderStatusResponse)
            : { ...(current as any), ...(patch as any) };

          get().setStatusesFor(
            key,
            merged as ProviderAndProviderStatusResponse
          );
        },

        getStatusesFor: (key) =>
          get().statusesByScope.find((e) => sameKey(e, key))?.statuses,

        removeStatusesFor: (key) => {
          set((state) => ({
            statusesByScope: state.statusesByScope.filter(
              (e) => !sameKey(e, key)
            ),
          }));
          channel?.postMessage({
            source: TAB_ID,
            type: 'remove-scope',
            payload: { key },
          });
        },

        initializedProviderConnection: null,
        setInitializedProviderConnection: (value) => {
          set({ initializedProviderConnection: value });
          channel?.postMessage({
            source: TAB_ID,
            type: 'set-conn',
            payload: { initializedProviderConnection: value },
          });
        },
      }),
      {
        name: 'provider-sync-status',
        storage: createJSONStorage(() => localStorage),
        version: 1,
        // Persist exactly what you need
        partialize: (s) => ({
          statusesByScope: s.statusesByScope,
          initializedProviderConnection: s.initializedProviderConnection,
        }),
      }
    ),
    { name: 'providerSyncStatus' }
  )
);

// Avoid duplicate listeners in dev/HMR
declare global {
  // eslint-disable-next-line no-var
  var __PROVIDER_SYNC_WIRED__: boolean | undefined;
}

if (!globalThis.__PROVIDER_SYNC_WIRED__) {
  globalThis.__PROVIDER_SYNC_WIRED__ = true;

  channel &&
    (channel.onmessage = (e) => {
      const data = e.data;
      if (!data || data.source === TAB_ID) return;

      const { type, payload } = data as
        | {
            type: 'upsert-scope';
            payload: { key: Key; statuses: ProviderAndProviderStatusResponse };
          }
        | { type: 'remove-scope'; payload: { key: Key } }
        | {
            type: 'set-conn';
            payload: { initializedProviderConnection: Provider | null };
          };

      if (type === 'upsert-scope') {
        useProviderSyncStatus.setState((state) => {
          const idx = state.statusesByScope.findIndex((e) =>
            sameKey(e, payload.key)
          );
          const statusesByScope =
            idx === -1
              ? [
                  ...state.statusesByScope,
                  { ...payload.key, statuses: payload.statuses },
                ]
              : [
                  ...state.statusesByScope.slice(0, idx),
                  { ...state.statusesByScope[idx], statuses: payload.statuses },
                  ...state.statusesByScope.slice(idx + 1),
                ];
          return { ...state, statusesByScope };
        });
      } else if (type === 'remove-scope') {
        useProviderSyncStatus.setState((state) => ({
          ...state,
          statusesByScope: state.statusesByScope.filter(
            (e) => !sameKey(e, payload.key)
          ),
        }));
      } else if (type === 'set-conn') {
        useProviderSyncStatus.setState((state) => ({
          ...state,
          initializedProviderConnection: payload.initializedProviderConnection,
        }));
      }
    });
}

/* --------------------------
   Usage examples
--------------------------- */
// Write/replace all statuses for a scope:
/// useProviderSyncStatus.getState().setStatusesFor({ locationId: 2, brandId: 0 }, nextStatuses);

// Read:
/// const s = useProviderSyncStatus((st) =>
///   st.getStatusesFor({ locationId: 2, brandId: 0 })
/// );

// Subscribe to one scope efficiently (optional):
// const selectScope = (key: Key) => (st: ProviderSyncStatusStore) =>
//   st.statusesByScope.find((e) => e.locationId === key.locationId && e.brandId === key.brandId)?.statuses;
// const shallowEq = (a: any, b: any) => Object.is(a, b);
// const statuses = useProviderSyncStatus(selectScope({ locationId: 2, brandId: 0 }), shallowEq);
